import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { Play, Plus, Users, Radio, UserX, XCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { createSession, listSessions, closeSession, openSessionSocket } from './liveApi';

const STATUS_LABELS = {
  lobby: { label: 'Lobby ouvert', variant: 'default' },
  in_progress: { label: 'En cours', variant: 'secondary' },
  closed: { label: 'Terminée', variant: 'outline' },
};

// Onglet « Live » du dashboard host : créer une session, piloter le lobby.
const HostLivePanel = () => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [config, setConfig] = useState({
    rounds: 3,
    narration_seconds: 90,
    require_auth: false,
  });

  const refresh = useCallback(async () => {
    try {
      const response = await listSessions();
      setSessions(response.data);
    } catch (error) {
      toast.error('Impossible de charger les sessions live.');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await createSession(config);
      toast.success(`Session créée — PIN ${response.data.pin}`);
      setShowCreate(false);
      setActiveSession(response.data);
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Création impossible.');
    } finally {
      setCreating(false);
    }
  };

  if (activeSession) {
    return (
      <HostLobby
        session={activeSession}
        onBack={() => { setActiveSession(null); refresh(); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Sessions live</h2>
          <p className="text-gray-600">Battles en direct, façon Kahoot : PIN, lobby, manches.</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle session
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer une session live</DialogTitle>
              <DialogDescription>
                Les participants rejoindront avec le code PIN généré.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rounds">Manches (1-10)</Label>
                  <Input id="rounds" type="number" min={1} max={10}
                    value={config.rounds}
                    onChange={(e) => setConfig({ ...config, rounds: Number(e.target.value) })} />
                </div>
                <div>
                  <Label htmlFor="narration">Narration (secondes)</Label>
                  <Input id="narration" type="number" min={30} max={300} step={15}
                    value={config.narration_seconds}
                    onChange={(e) => setConfig({ ...config, narration_seconds: Number(e.target.value) })} />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label htmlFor="require-auth">Exiger un compte pour rejoindre</Label>
                  <p className="text-xs text-gray-500">
                    Désactivé : accès anonyme par PIN + pseudo (recommandé).
                  </p>
                </div>
                <Switch id="require-auth"
                  checked={config.require_auth}
                  onCheckedChange={(checked) => setConfig({ ...config, require_auth: checked })} />
              </div>
              <Button onClick={handleCreate} disabled={creating} className="w-full">
                {creating ? 'Création…' : 'Créer et ouvrir le lobby'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sessions.map((session) => {
          const status = STATUS_LABELS[session.status] || STATUS_LABELS.closed;
          return (
            <Card key={session.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-mono tracking-widest">
                    PIN {session.pin}
                  </CardTitle>
                  <Badge variant={status.variant}>{status.label}</Badge>
                </div>
                <CardDescription>
                  Créée le {new Date(session.created_at).toLocaleString('fr-FR')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {session.status !== 'closed' && (
                  <Button size="sm" onClick={() => setActiveSession(session)}>
                    <Radio className="w-4 h-4 mr-2" />
                    Ouvrir le pilotage
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
        {sessions.length === 0 && (
          <Card className="md:col-span-2">
            <CardContent className="py-10 text-center text-gray-500">
              Aucune session live. Créez-en une pour obtenir un code PIN.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Écran de pilotage du lobby par le host (présences, kick, démarrage).
const HostLobby = ({ session, onBack }) => {
  const [participants, setParticipants] = useState([]);
  const [status, setStatus] = useState(session.status);
  const socketRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const socket = openSessionSocket(session.id, token, {
      onMessage: (message) => {
        switch (message.type) {
          case 'lobby_state':
            setParticipants(message.participants);
            setStatus(message.session_status);
            break;
          case 'participant_joined':
            setParticipants((current) => {
              const others = current.filter(
                (p) => p.participation_id !== message.participation_id
              );
              return [...others, { ...message, connected: true }];
            });
            break;
          case 'participant_left':
            setParticipants((current) =>
              current.filter((p) => p.participation_id !== message.participation_id)
            );
            break;
          case 'session_started':
            setStatus('in_progress');
            toast.success('Session démarrée !');
            break;
          case 'error':
            toast.error(message.detail);
            break;
          default:
            break;
        }
      },
      onClose: () => toast.error('Connexion temps réel fermée.'),
    });
    socketRef.current = socket;
    return () => socket.close();
  }, [session.id]);

  const storytellers = participants.filter((p) => p.role === 'storyteller');

  const handleStart = () => {
    if (storytellers.length === 0) {
      toast.error('Il faut au moins un conteur pour démarrer.');
      return;
    }
    socketRef.current.send({ type: 'start' });
  };

  const handleKick = (participation) => {
    socketRef.current.send({ type: 'kick', participation_id: participation.participation_id });
  };

  const handleClose = async () => {
    try {
      await closeSession(session.id);
      toast.success('Session clôturée.');
      onBack();
    } catch (error) {
      toast.error('Clôture impossible.');
    }
  };

  const joinUrl = `${window.location.origin}/join`;
  const statusInfo = STATUS_LABELS[status] || STATUS_LABELS.closed;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux sessions
        </Button>
        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
      </div>

      {/* Écran « projection » : PIN en très grand (CDC §9) */}
      <Card className="bg-indigo-600 text-white">
        <CardContent className="py-10 text-center space-y-3">
          <p className="text-lg opacity-80">Rejoignez sur <strong>{joinUrl}</strong> avec le code</p>
          <p className="text-7xl font-extrabold font-mono tracking-[0.2em]">
            {session.pin}
          </p>
          <p className="opacity-80">
            <Users className="w-4 h-4 inline mr-1" />
            {participants.length} connecté{participants.length > 1 ? 's' : ''} —{' '}
            {storytellers.length} conteur{storytellers.length > 1 ? 's' : ''}
          </p>
        </CardContent>
      </Card>

      <div className="flex space-x-3">
        <Button onClick={handleStart} disabled={status !== 'lobby'} className="h-12 px-8 text-lg">
          <Play className="w-5 h-5 mr-2" />
          Démarrer le battle
        </Button>
        <Button variant="outline" onClick={handleClose}>
          <XCircle className="w-4 h-4 mr-2" />
          Clôturer
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Participants</CardTitle>
          <CardDescription>Les arrivées et départs s'affichent en direct.</CardDescription>
        </CardHeader>
        <CardContent>
          {participants.length === 0 ? (
            <p className="text-gray-500 text-center py-6">
              Personne pour l'instant — partagez le PIN !
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {participants.map((participant) => (
                <div key={participant.participation_id}
                  className="flex items-center justify-between rounded-lg border p-2">
                  <div className="flex items-center space-x-2 min-w-0">
                    <span className={`h-2 w-2 rounded-full shrink-0 ${participant.connected ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="truncate">{participant.nickname}</span>
                    <Badge variant="secondary" className="shrink-0">
                      {participant.role === 'storyteller' ? 'Conteur' : 'Public'}
                    </Badge>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleKick(participant)}
                    title="Retirer ce participant">
                    <UserX className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HostLivePanel;
